import { useMemo } from "react";
import { useLocation } from "react-router";
import { cnbConfigSchema, defaultConfig, type cnbConfigType } from "@core/model/cnbModel";
import { parseSchema } from "@core/model/parseSchema";

function getActiveIndexPath(menus: cnbConfigType["menu"], pathname: string): number[] {
  let activeIndex: number[] = [];

  const find = (items: cnbConfigType["menu"], currentDepth = 0, depthArr: number[] = []): boolean => {
    return items?.some((item, index) => {
      if (item.url) {
        const allMatches = [item.url, ...(item.matches || [])];

        // 현재 메뉴 매치 여부 확인
        const isMatched = allMatches.some((s) => (item.useExactMatch ? s === pathname : new RegExp(s).test(pathname)));

        // 현재 인덱스 기록 (이 위치에서 push해줘야 되돌릴 수 있음)
        depthArr[currentDepth] = index;

        if (!item.children && isMatched) {
          activeIndex = [...depthArr];
          return true; // 매치 시 탐색 종료
        }

        // 자식 탐색
        if (item.children) {
          const foundInChild = find(item.children, currentDepth + 1, depthArr);
          if (foundInChild) return true;
        }
      }

      depthArr[currentDepth] = 0;
      return false;
    });
  };

  find(menus);
  return activeIndex;
}

export const useCnbConfig = () => {
  const data = useMemo(() => {
    const cnbConfig: cnbConfigType = { ...defaultConfig, ...window.cnbConfig };
    return parseSchema({
      schema: cnbConfigSchema,
      rawData: cnbConfig,
    });
  }, []);

  const location = useLocation();

  // depth1~3까지 활성화된 메뉴 인덱스 array
  const activeIndex = useMemo(() => {
    return getActiveIndexPath(data?.menu ?? [], location.pathname);
  }, [data, location.pathname]);

  // 현재 활성화된 1뎁스 데이터
  const activeMenuData = useMemo(() => {
    return data?.menu?.[activeIndex[0]];
  }, [data?.menu, activeIndex]);

  const depth1 = useMemo(() => data?.menu, [data?.menu]);
  const depth2 = useMemo(() => depth1?.[activeIndex[0]]?.children, [depth1, activeIndex]);
  const depth3 = useMemo(() => depth2?.[activeIndex[1]]?.children, [depth2, activeIndex]);

  // Mobile - LNB 구성
  const menuDepthMap = useMemo(
    () => ({
      depth1,
      depth2,
      depth3,
    }),
    [depth1, depth2, depth3],
  );

  return { data, activeIndex, activeMenuData, menuDepthMap };
};
